/**
 * Per-goal bet bag. Mirrors
 *   packages/amgfootball_v2/lib/domain/entities/bet.dart (BetCollection)
 * — entries accumulate by betId, toApiFormat emits { amount, betItemId }.
 */
import type { BetType } from '../types/game';
import { BET_ITEM_ID } from '../types/game';

export interface BetEntry {
  bet: BetType;
  amount: number;
  chips: number[]; // history of chip denominations placed
}

export class BetCollection {
  private entries: Map<BetType, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { bet: v.bet, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(bet: BetType, amount: number): void {
    const cur = this.entries.get(bet);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(bet, { bet, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(bet: BetType): void {
    this.entries.delete(bet);
  }

  amountFor(bet: BetType): number {
    return this.entries.get(bet)?.amount ?? 0;
  }

  chipsFor(bet: BetType): readonly number[] {
    return this.entries.get(bet)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get bets(): readonly BetType[] {
    return Array.from(this.entries.keys());
  }

  /**
   * Convert into the body lines expected by /action/bet. Field name is
   * `amount` (NOT `betAmount`); only entries with amount > 0.
   */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: BET_ITEM_ID[v.bet], amount: v.amount });
    }
    return out;
  }

  /**
   * Like toBetLines() but emits ONE line per INDIVIDUAL chip placed rather
   * than one line carrying the per-goal total. Used for multiplayer
   * submission via the SignalR hub, which validates each amount against the
   * configured chip denominations and rejects a summed total (e.g. 300) with
   * "Invalid Chip Amount". Submitting one PlaceBet per chip keeps every amount
   * a valid denomination. Uses the IDENTICAL betItemId derivation
   * (BET_ITEM_ID[v.bet]) and the same v.amount <= 0 skip as toBetLines(); for
   * each betItemId, the sum of these amounts equals the toBetLines amount.
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      for (const chip of v.chips) {
        if (chip <= 0) continue;
        out.push({ betItemId: BET_ITEM_ID[v.bet], amount: chip });
      }
    }
    return out;
  }
}
